import { forwardRef } from "react";
import { Button, Flex, Skeleton } from "antd";
import vivoIconUrl from "@/assets/icons/vivo_logo_white.svg";
import VivoLoadIcon from "@/assets/icons/vivoLoading.gif";
import ChatButtonGroup from "./ChatButtonGroup";

import MarkdownRenderer from "../UI/MarkdownRenderer";
import ChatPlotRenderer from "./ChatPlotRenderer";
import type { ChatItemProps, ChatMessage } from "./chat";
import { filterDisplayableAgentSources } from "@/utils/chatSourceFilters";
import { stripContextPrefix } from "@/components/AskVivo/askVivo.utils";
import { formatDateText } from "@/utils/dateUtils";
import {
  CHAT_STATUS,
  hasNoUsableSources,
  isAwaitingClarification,
  normalizeChatStatus,
} from "./utils";

type ChatResponseContentProps = {
  chat: NonNullable<ChatItemProps["chat"]>;
  chatStatus: (typeof CHAT_STATUS)[keyof typeof CHAT_STATUS];
};

function ChatResponseContent({ chat, chatStatus }: ChatResponseContentProps) {
  if (chatStatus === CHAT_STATUS.PENDING) {
    return (
      <div
        data-testid="chat-response-pending"
        className="answer-box mt-1 mb-4 bg-amber-100 border-amber-300 text-orange-950 p-4 rounded-sm"
      >
        Your response is still in progress. Please check back shortly.
      </div>
    );
  }

  if (chat.output) {
    return (
      <>
        <div
          data-testid={
            chatStatus === CHAT_STATUS.ERROR
              ? "chat-response-error"
              : "chat-response-content"
          }
          className="answer-box mt-1 markdown"
        >
          <MarkdownRenderer content={chat.output} />
          <div className="flex flex-col">
            {filterDisplayableAgentSources(
              chat.selected_sources?.PlotAgentSchema,
            ).map((plotSource, plotIndex) =>
              plotSource?.citation?.source_url ? (
                <ChatPlotRenderer
                  key={
                    plotSource.citation.source_url ||
                    `${chat.interaction_id || "chat"}-plot-${plotIndex}`
                  }
                  url={plotSource.citation.source_url}
                />
              ) : null,
            )}
          </div>
        </div>
        {chat.timestamp && (
          <span className="text-[11px] text-[#8097A0] mb-3">
            {formatDateText(chat.timestamp)}
          </span>
        )}
      </>
    );
  }

  if (chatStatus === CHAT_STATUS.ERROR) {
    return (
      <div
        data-testid="chat-response-error"
        className="answer-box mt-1 mb-4 markdown"
      >
        {chat.output ||
          "There was an error processing your request. Please try again."}
      </div>
    );
  }

  return (
    <div
      data-testid="chat-response-unavailable"
      className="answer-box mt-1 mb-4 markdown"
    >
      Response not available yet. Please come back later.
    </div>
  );
}

const ChatItem = forwardRef<HTMLDivElement, ChatItemProps>(function ChatItem(
  {
    userFirstInitial = "",
    chat,
    handleViewSearchSources,
    handleRegenerate,
    chatHistory = [],
    index = 0,
    selectedSources = [],
    viewSourcesOpen,
    handleOpenCreateInsights,
    copiedInteractionId,
    setCopiedInteractionId,
    buttonSize = "small",
    ...props
  },
  ref,
) {
  const copied = copiedInteractionId === chat?.interaction_id;
  const chatStatus = normalizeChatStatus(chat);

  const handleCopyText = () => {
    const interactionId = chat?.interaction_id;
    if (!interactionId) return;

    const element = document.getElementById(interactionId);
    if (!element) return;

    const text = element.innerHTML || "";

    async function copyContent() {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedInteractionId(interactionId ?? null);
      } catch (err) {
        console.error("Failed to copy", err);
        setCopiedInteractionId(null);
      }
    }
    copyContent();
  };

  if (!chat) {
    return (
      <div ref={ref} {...props}>
        <div className="flex gap-6 mt-5 mb-5">
          <Button
            shape="circle"
            size="medium"
            icon={<img src={vivoIconUrl} width={16} height={10} alt="" />}
            className="ergo-button"
          />
          <div className="answer-box mt-1 mb-4 markdown">
            No chat data available. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const shouldShowChatActions =
    chatStatus === CHAT_STATUS.COMPLETE &&
    !isAwaitingClarification(chat) &&
    !hasNoUsableSources(chat);

  return (
    <div ref={ref} {...props} className="pt-3">
      <div className="flex gap-6 mb-3 min-w-0">
        <Button
          shape="circle"
          size="medium"
          className="initials-button shrink-0"
        >
          {userFirstInitial || "?"}
        </Button>
        <div className="prompt-box min-w-0">
          {stripContextPrefix(chat.input || "")}
        </div>
      </div>
      <div className="flex gap-6 mt-5 mb-5 min-w-0">
        <Button
          shape="circle"
          size="medium"
          icon={
            chat.isLoading ? (
              <img src={VivoLoadIcon} alt="Loading..." />
            ) : (
              <img
                src={vivoIconUrl}
                width={16}
                height={10}
                alt=""
                className="mb-px"
              />
            )
          }
          className="ergo-button shrink-0"
        />
        {chat.isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} className="min-w-0 flex-1" />
        ) : (
          <>
            <div
              id={chat.interaction_id || "default"}
              style={{ display: "none" }}
            >
              {chat.output || ""}
            </div>
            <Flex vertical className="min-w-0 flex-1">
              <ChatResponseContent chat={chat} chatStatus={chatStatus} />
              {shouldShowChatActions && (
                <div className="flex items-center gap-2 flex-wrap">
                  <ChatButtonGroup
                    handleViewSearchSources={
                      handleViewSearchSources as (
                        chat: ChatMessage,
                        index: number,
                      ) => void
                    }
                    chat={chat}
                    copied={copied}
                    handleCopyText={handleCopyText}
                    lastItem={chatHistory?.length - 1 === index}
                    handleRegenerate={
                      handleRegenerate as (
                        sessionId: string,
                        input: string,
                        selectedSources: unknown,
                      ) => void
                    }
                    selectedSources={selectedSources}
                    viewSourcesOpen={viewSourcesOpen ?? false}
                    index={index}
                    handleOpenCreateInsights={
                      handleOpenCreateInsights as (chat: ChatMessage) => void
                    }
                    buttonSize={
                      buttonSize as "small" | "middle" | "large" | undefined
                    }
                  />
                </div>
              )}
            </Flex>
          </>
        )}
      </div>
    </div>
  );
});

export default ChatItem;
